import React, { useCallback, useEffect, useState } from "react";
import { PosTile } from "./PosTile";
import { api } from "../../../software/api";
import { rankParts } from "../workorders/shared";
import { toast } from "sonner";
import Receipt from "./Receipt";
import { ensureCompanyInfo } from "../reports/legacy/docs";
import StartDayFloat from "./StartDayFloat";
import EndOfDay from "./EndOfDay";
import { PettyCashOut, PettyCashBack } from "./PettyCash";
import RefundPopup from "./RefundPopup";
import { InvoicePaymentHow } from "../client/InvoicePaymentHow";
import { Search, X, Trash2, Printer, Barcode, Plus, KeyRound, Users, Banknote, CreditCard, Split, FileText } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const base = process.env.REACT_APP_BACKEND_URL;
const QTY_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20];
const DISCOUNTS = [5, 10, 15, 20];

export default function Pos({ branding, user, onNav, entry, onEntryConsumed, onOpenClient, onAddClient, layoutKey }) {
  const [view, setView] = useState("clerk"); // clerk | register
  const [mode, setMode] = useState("counter"); // counter | client
  const [client, setClient] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [credit, setCredit] = useState(0);
  useEffect(() => { if (!client?.id) { setCredit(0); return; } api("GET", `/clients/${client.id}`).then((c) => setCredit(c.store_credit || 0)).catch(() => setCredit(0)); }, [client?.id]);
  const [attn, setAttn] = useState(null);
  const [clerk, setClerk] = useState(user?.name || "Clerk");
  const [team, setTeam] = useState([]);
  const [changeClerk, setChangeClerk] = useState(false);

  const [tiles, setTiles] = useState([]);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [tabId, setTabId] = useState(null);
  useEffect(() => { api("GET", "/pos-tabs").then((t) => { setTabs(t); setTabId((x) => (t.some((y) => y.id === x) ? x : t[0]?.id)); }).catch(() => {}); }, [layoutKey]);
  useEffect(() => { if (layoutKey) loadTiles(); }, [layoutKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [discountPct, setDiscountPct] = useState(0);
  const [completed, setCompleted] = useState(null);

  const [priceCheck, setPriceCheck] = useState(false);
  const [loyalty, setLoyalty] = useState(false);
  const [qtyPad, setQtyPad] = useState(null); // {name, unit_price, product_id}
  const [pay, setPay] = useState(null); // 'cash' | 'split' | 'card:<type>'
  const [manual, setManual] = useState(false);
  const [dbPick, setDbPick] = useState(false);
  const [invoicePay, setInvoicePay] = useState(false);
  const [purchasesOpen, setPurchasesOpen] = useState(false);


  const loadTiles = useCallback(async () => { try { setTiles(await api("GET", "/pos-tiles")); } catch { setTiles([]); } }, []);
  const loadProducts = useCallback(async () => { try { setProducts(await api("GET", "/products")); } catch { setProducts([]); } }, []);
  const [aliases, setAliases] = useState([]);
  const [clerks, setClerks] = useState([]);
  const [eodOpen, setEodOpen] = useState(false);
  const [petty, setPetty] = useState(null); // out | back
  useEffect(() => { ensureCompanyInfo(); loadTiles(); loadProducts(); api("GET", "/team").then(setTeam).catch(() => {}); api("GET", "/pos/clerks").then(setClerks).catch(() => {}); api("GET", "/product-aliases").then(setAliases).catch(() => {}); }, [loadTiles, loadProducts]);

  const matchProduct = useCallback((name, brand, partId) => {
    const n = String(name || "").toLowerCase().trim();
    if (!n) return null;
    const byId = partId && products.find((x) => x.id === partId || x.sku === partId);
    if (byId) return byId;
    const exact = products.filter((x) => (x.name || "").toLowerCase().trim() === n);
    const exactPriced = exact.find((x) => (x.price || 0) > 0);
    if (exactPriced) return exactPriced;
    const b = String(brand || "").toLowerCase().trim();
    const hit = (av) => av && (av === n || n.includes(av) || av.includes(n));
    const cand = aliases.filter((a) => hit((a.alias || "").toLowerCase()));
    const prod = (a) => products.find((x) => x.id === a.product_id);
    const priced = (a) => { const p = prod(a); return p && (p.price || 0) > 0; };
    const brandCand = b ? cand.filter((a) => (a.brand || "").toLowerCase() === b) : [];
    const pool = brandCand.length ? brandCand : cand.filter((a) => !(a.brand || "")).concat(cand);
    const chosen = pool.find(priced) || pool[0];
    if (chosen) { const p = prod(chosen); if (p && ((p.price || 0) > 0 || !exact.length)) return p; }
    const fuzzy = products.filter((x) => { const pn = (x.name || "").toLowerCase(); return pn && (pn === n || pn.includes(n) || n.includes(pn)); });
    const bf = b ? fuzzy.filter((x) => (x.brand || "").toLowerCase() === b) : [];
    const list = bf.length ? bf : fuzzy;
    const best = list.find((x) => (x.price || 0) > 0) || list[0];
    if (best && ((best.price || 0) > 0 || !exact.length)) return best;
    if (exact.length) return exact[0];
    // last resort: every meaningful word (singular) of the name appears in the product name, e.g. "Dead Sea Mineral Salt (10kg)" -> "Dead Sea Minerals 10kg Bag"
    const words = (s) => s.toLowerCase().replace(/[^a-z0-9.]+/g, " ").split(" ").filter((w) => w.length > 1 && !["salt", "bag", "the", "of"].includes(w)).map((w) => w.replace(/s$/, ""));
    const want = words(n);
    const tok = want.length >= 2 && products.filter((x) => { const have = new Set(words(x.name || "")); return want.every((w) => have.has(w)); });
    return (tok && (tok.find((x) => (x.price || 0) > 0) || tok[0])) || null;
  }, [aliases, products]);

  const displayTiles = tiles.length ? tiles : products.filter((p) => p.active !== false).slice(0, 24).map((p) => ({ id: p.id, product_id: p.id, label: p.name, image_url: p.image_url, price: p.price, kind: "normal" }));

  const subtotal = cart.reduce((s, l) => s + l.qty * l.unit_price, 0);
  // Discount applies to POSITIVE lines only — refunds/credits (negative lines) are never discounted.
  const positiveSubtotal = cart.reduce((s, l) => s + Math.max(l.qty * l.unit_price, 0), 0);
  const discountAmt = Math.round(positiveSubtotal * discountPct) / 100;
  const total = subtotal - discountAmt;

  const addLine = (line) => setCart((c) => [...c, line]);
  const addOrMerge = (line) => setCart((c) => {
    const i = c.findIndex((l) => l.product_id && l.product_id === line.product_id && l.unit_price === line.unit_price);
    if (i >= 0) { const n = [...c]; n[i] = { ...n[i], qty: n[i].qty + line.qty }; return n; }
    return [...c, line];
  });
  const removeLine = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const tapTile = (t) => {
    const priceOf = (pid, fb) => { const p = products.find((x) => x.id === pid); return p ? p.price : (fb || 0); };
    if (t.kind === "container_refund") { addLine({ name: "15L Container Refund", unit_price: -(t.deposit || 11), qty: 1, product_id: null }); return; }
    if (!t.product_id || !t.part_name) { toast.error("This tile isn't linked to a part yet — fix it in POS Layout"); return; }
    const price = t.part_price ?? priceOf(t.product_id, 0);
    const name = t.part_name;
    if (t.kind === "drum_no_return") { addLine({ name, unit_price: price, qty: 1, product_id: t.product_id }); return; }
    if (t.kind === "drum_return") {
      addLine({ name, unit_price: price, qty: 1, product_id: t.product_id });
      addLine({ name: "Container Deposit Refund", unit_price: -(t.deposit || 11), qty: 1, product_id: null });
      return;
    }
    setQtyPad({ name, unit_price: price, product_id: t.product_id });
  };
  const confirmQty = (n) => { if (qtyPad) addLine({ ...qtyPad, qty: n }); setQtyPad(null); };

  const startCounter = () => { setMode("counter"); setClient(null); setAttn(null); setDiscountPct(0); setCart([]); setView("register"); };
  const startClient = (c) => { setMode("client"); setClient(c); setAttn((c.contacts || []).find((x) => x.billing) || null); setDiscountPct(c.discount_rate > 0 ? c.discount_rate : 0); setCart([]); setLoyalty(false); setView("register"); };

  const [pending, setPending] = useState(null); // {client, items, brand} awaiting clerk selection
  const [entryDone, setEntryDone] = useState(false);
  const [showFloat, setShowFloat] = useState(false); // Start-of-Day Till Float popup
  const [showMenu, setShowMenu] = useState(false); // POS Selection popup (over Select Clerk)
  const [menuQuery, setMenuQuery] = useState("");
  const [menuResults, setMenuResults] = useState([]);
  const [menuSearching, setMenuSearching] = useState(false);
  useEffect(() => {
    if (!entry || entryDone) return;
    setPending({ client: entry.client, items: entry.items || [], brand: entry.brand || null });
    setView("clerk");
    setEntryDone(true);
    onEntryConsumed && onEntryConsumed();
  }, [entry, entryDone]); // eslint-disable-line

  const applyPending = (p) => {
    startClient(p.client);
    const matched = []; const missing = [];
    // items: names or { name, qty } (pack / mineral lines from the water test)
    (p.items || []).forEach((it) => {
      const name = typeof it === "string" ? it : it.name, qty = (typeof it === "object" && Number(it.qty)) || 1;
      const prod = matchProduct(name, p.brand, typeof it === "object" ? it.part_id : null);
      const dup = prod && matched.find((m) => m.product_id === prod.id);
      if (dup) dup.qty += qty;
      else if (prod) matched.push({ name: prod.name, unit_price: prod.price || 0, qty, product_id: prod.id });
      else if (!missing.includes(name)) missing.push(name);
    });
    setTimeout(() => setCart(matched), 0);
    if (missing.length) toast.info(`Recommended items not in catalogue: ${missing.join(", ")} — add them manually.`);
    else if (matched.length) toast.success(`${matched.length} recommended item(s) added to the sale.`);
  };

  // Clerk tapped on the Select Clerk gate. If the Start-of-Day Till Float has not yet
  // been recorded for today, show the Float popup first (legacy Forms_CashDrawer rule).
  const pickClerk = async (name) => {
    setClerk(name);
    if (pending) { applyPending(pending); setPending(null); return; }
    try {
      const t = await api("GET", "/cashdrawer/today");
      if (!t.started) { setShowFloat(true); return; }
    } catch (e) { /* if the check fails, don't block the sale */ }
    setShowMenu(true);
  };

  // debounced client search inside the POS Selection popup (loyalty / surname / street / mobile)
  useEffect(() => {
    if (!showMenu) return;
    const q = menuQuery.trim();
    if (!q) { setMenuResults([]); setMenuSearching(false); return; }
    setMenuSearching(true);
    const t = setTimeout(async () => {
      try { const r = await api("GET", `/clients?q=${encodeURIComponent(q)}&limit=25`); setMenuResults(r || []); }
      catch { setMenuResults([]); }
      finally { setMenuSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [menuQuery, showMenu]);

  const closeMenu = () => { setShowMenu(false); setMenuQuery(""); setMenuResults([]); };
  const clientLoyalty = (c) => (c.loyalty_number || "").trim() || (/^[0-9]+$/.test((c.status || "").trim()) ? (c.status || "").trim() : "");
  const openClientWaterTest = (c) => { closeMenu(); onOpenClient && onOpenClient(c.id, "water_tests"); };
  const menuEnter = () => {
    const q = menuQuery.trim();
    if (!menuResults.length) { toast.info("No client found — try surname, street, mobile, or tap Add New Client."); return; }
    // a scanned/typed loyalty number wins over a legacy-id or partial match on other fields
    const byLoyalty = menuResults.find((c) => [c.loyalty_number, c.status].some((v) => String(v || "").trim() === q));
    const byLegacy = menuResults.find((c) => String(c.legacy_id || "").trim() === q);
    openClientWaterTest(byLoyalty || byLegacy || menuResults[0]);
  };

  const clear = () => { setCart([]); setDiscountPct(mode === "client" && client?.discount_rate ? client.discount_rate : 0); };  const exitPos = () => { setView("clerk"); setShowMenu(true); setCart([]); setClient(null); setAttn(null); setMode("counter"); setDiscountPct(0); };

  const salePayload = (payments, asInvoice = false) => ({
    client_id: client?.id || null, client_name: client?.name || "",
    items: cart.map((l) => ({ product_id: l.product_id, name: l.name, sku: "", qty: l.qty, unit_price: l.unit_price, discount: 0 })),
    payments, discount: discountAmt, amount_tendered: payments.reduce((s, p) => s + p.amount, 0),
    note: `Clerk: ${clerk}`, as_invoice: asInvoice, clerk,
    addressed_to: attn ? `${attn.first_name || ""} ${attn.surname || ""}`.trim() : "",
    addressed_relationship: attn?.relationship || "", addressed_email: attn?.email || "",
  });

  const finishSale = async (payments) => {
    try { const sale = await api("POST", "/sales", salePayload(payments)); setPay(null); setCompleted(sale); clear(); toast.success(`Sale ${sale.sale_number} complete`); }
    catch (e) { toast.error(e.message); }
  };
  const invoiceClient = async () => {
    if (!client) { toast.error("Attach a client first (Loyalty search)."); return; }
    try { const sale = await api("POST", "/sales", salePayload([], true)); setCompleted(sale); clear(); toast.success(`Invoice ${sale.sale_number} created — email will send once Resend is connected.`); }
    catch (e) { toast.error(e.message); }
  };
  const openDrawer = async () => { try { await api("POST", "/pos/open-drawer"); toast.success("Till drawer opened (No Sale)"); } catch (e) { toast.error(e.message); } };
  const buyAgain = (row) => {
    const p = matchProduct(row.product);
    addOrMerge({ name: p ? p.name : row.product, unit_price: p ? (p.price || 0) : (row.price || 0), qty: 1, product_id: p ? p.id : null });
    toast.success(`${row.product} added to the sale`);
  };

  // ---------- SELECT CLERK GATE (resting state) + POS SELECTION POPUP ----------
  if (view === "clerk") {
    return (
      <div data-testid="pos-clerk-gate" className="relative min-h-[70vh] rounded-xl p-5 flex flex-col overflow-hidden"
        style={{ backgroundImage: "linear-gradient(180deg,rgba(220,236,250,.82),rgba(188,216,240,.88)), url('/select-clerk-bg.png')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="relative flex items-center justify-between mb-3">
          <h2 className="font-display font-black text-xl text-[#123a63]">Select Clerk</h2>
          <button onClick={() => onNav && onNav("tab:switchboard")} data-testid="pos-clerk-switchboard" className="inline-flex items-center gap-1.5 rounded-lg border-2 border-[#123a63] bg-white px-4 py-2 text-sm font-black text-[#123a63] hover:bg-slate-50">Switch Menu</button>
        </div>
        <p className="relative text-[13px] text-slate-700 mb-4 font-medium">Who is serving this customer? Tap your name to start.</p>
        {pending?.client && <p className="relative text-[13px] text-[#123a63] mb-4 -mt-3 font-bold" data-testid="pos-clerk-pending-client">Counter sale for: {pending.client.name}</p>}
        <div className="relative mx-auto w-full max-w-md bg-white/95 border border-[#5b8ac4] rounded-lg shadow-lg p-2">
          {clerks.length === 0 && <p className="text-slate-400 text-sm p-6 text-center">Loading staff…</p>}
          {clerks.map((t) => (
            <button key={t.id} onClick={() => pickClerk(t.name)} data-testid={`pos-clerk-pick-${t.id}`}
              className="block w-full text-center text-[17px] font-bold text-[#123a63] py-2.5 border-b border-sky-100 last:border-0 hover:bg-sky-50 rounded">
              {t.name}{t.position ? <span className="text-slate-400 font-normal text-[13px]"> — {t.position}</span> : null}
            </button>
          ))}
        </div>

        {showFloat && (
          <StartDayFloat clerk={clerk} company={branding?.company}
            onClose={() => setShowFloat(false)}
            onDone={() => { setShowFloat(false); setView("clerk"); }} />
        )}

        {showMenu && (
          <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-auto bg-black/55 backdrop-blur-sm" data-testid="pos-menu">
            <div className="w-full max-w-5xl my-6 rounded-2xl bg-gradient-to-b from-[#eaf3fc] to-[#cfe2f5] border border-[#5b8ac4] shadow-2xl">
              {/* header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#9fc0e2]">
                <div className="flex items-center gap-3">
                  <button onClick={() => { closeMenu(); }} data-testid="pos-change-clerk" className="rounded-lg border-2 border-emerald-500 bg-white px-4 py-2 text-sm font-black text-emerald-700 hover:bg-emerald-50">Change Clerk</button>
                  <span className="text-[13px] text-slate-600">Clerk: <b className="text-[#123a63]">{clerk}</b></span>
                </div>
                <h1 className="font-display text-2xl font-black text-[#123a63] underline tracking-wide">POINT OF SALE TERMINAL</h1>
                <button onClick={() => onNav && onNav("tab:switchboard")} data-testid="pos-switch-menu" className="rounded-lg border border-slate-400 bg-slate-100 px-4 py-2 text-sm font-bold shadow hover:bg-slate-50">Switch Menu</button>
              </div>

              <div className="p-5">
                {/* PRIMARY — client search (loyalty / surname / street / mobile) -> Water Test */}
                <div className="rounded-xl border-4 border-[#c98f00] bg-[#fff6d6] p-4 shadow-inner" data-testid="pos-menu-search-panel">
                  <label className="block text-center font-display font-black text-[#123a63] text-xl mb-2">SCAN LOYALTY CARD &nbsp;·&nbsp; or type Loyalty&nbsp;# / Surname / Street / Mobile</label>
                  <div className="flex gap-2 max-w-3xl mx-auto">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-6 w-6 text-slate-400" />
                      <input
                        autoFocus
                        value={menuQuery}
                        onChange={(e) => setMenuQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") menuEnter(); }}
                        placeholder="Start typing or scan a card, then press ENTER…"
                        data-testid="pos-menu-search"
                        className="w-full bg-white border-2 border-[#c98f00] rounded-lg pl-12 pr-4 py-3.5 text-xl font-bold text-[#123a63] outline-none focus:ring-4 focus:ring-amber-300"
                      />
                    </div>
                    <button onClick={() => { onAddClient && onAddClient(); closeMenu(); }} data-testid="pos-add-new-client"
                      className="shrink-0 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-black text-base px-5 py-3.5 shadow-lg whitespace-nowrap">
                      + ADD NEW CLIENT TO SYSTEM
                    </button>
                  </div>
                  {menuQuery.trim() && (
                    <div className="max-w-3xl mx-auto mt-3 bg-white border border-[#9fc0e2] rounded-lg shadow max-h-72 overflow-auto" data-testid="pos-menu-results">
                      {menuSearching && <p className="text-slate-400 text-sm p-4 text-center">Searching…</p>}
                      {!menuSearching && menuResults.length === 0 && <p className="text-slate-400 text-sm p-4 text-center">No match — try another detail or tap <b>Add New Client</b>.</p>}
                      {!menuSearching && menuResults.map((c) => (
                        <button key={c.id} onClick={() => openClientWaterTest(c)} data-testid={`pos-menu-result-${c.id}`}
                          className="flex items-center justify-between w-full text-left px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-sky-50">
                          <span className="font-bold text-[#123a63] text-[15px]">{c.name}</span>
                          <span className="text-[12px] text-slate-500">{clientLoyalty(c) ? `Loyalty ${clientLoyalty(c)} · ` : ""}{(c.phone || c.phones?.[0] || c.contacts?.[0]?.mobile || "")}{c.address?.suburb ? ` · ${c.address.suburb}` : ""}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-center text-[12px] text-slate-600 mt-2">Opens the client straight on their <b>Water Test</b> tab — ready to test.</p>
                </div>

                {/* big actions */}
                <div className="mt-5 grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-5">
                  <div className="space-y-3">
                    <button onClick={() => setPriceCheck(true)} data-testid="pos-price-check" className="w-full rounded-lg bg-[#1f3a63] text-white p-4 text-left shadow hover:bg-[#16508a]"><span className="inline-block bg-red-600 text-white font-black px-2 py-0.5 rounded">Price Check</span><span className="block text-[11px] mt-2 opacity-80">Start Shopping</span></button>
                    <button onClick={() => onNav && onNav("tab:sales")} data-testid="pos-reports" className="w-full rounded-md border border-purple-300 bg-white px-3 py-2.5 text-[13px] font-black text-purple-700 hover:bg-purple-50">POS REPORTS</button>
                    <button onClick={() => setPetty("out")} data-testid="pos-petty-out" className="w-full rounded-md bg-sky-600 text-white px-3 py-2.5 text-[13px] font-bold hover:bg-sky-700">GET PETTY CASH OUT</button>
                    <button onClick={() => setPetty("back")} data-testid="pos-petty-change" className="w-full rounded-md bg-emerald-600 text-white px-3 py-2.5 text-[13px] font-bold hover:bg-emerald-700">PETTY CASH CHANGE BACK</button>
                    <button onClick={() => setEodOpen(true)} data-testid="pos-end-of-day" className="w-full rounded-md border border-slate-400 bg-slate-800 text-white px-3 py-2.5 text-[13px] font-bold hover:bg-slate-900">END OF DAY</button>
                    <button onClick={openDrawer} data-testid="pos-open-drawer" title="Open till drawer (No Sale)" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-white border border-slate-300 px-3 py-2.5 text-[13px] font-bold shadow hover:bg-slate-50"><KeyRound className="h-5 w-5 text-amber-600" /> OPEN DRAWER (No Sale)</button>
                  </div>

                  <div className="flex flex-col items-center gap-4">
                    <button onClick={() => { closeMenu(); startCounter(); }} data-testid="pos-over-counter" className="w-full max-w-md rounded-2xl bg-[#e08a1e] text-white font-black text-4xl py-10 shadow-lg hover:bg-[#c9791a] border-4 border-white/60">OVER<br/>COUNTER<br/>SALE</button>
                    <button onClick={() => setInvoicePay(true)} data-testid="pos-client-invoice-payment" className="w-full max-w-md rounded-md bg-purple-800 text-white font-black text-xl py-3 shadow hover:bg-purple-900">CLIENT INVOICE PAYMENT</button>
                    <p className="text-center font-black text-[#123a63]">10% OFF ALL IN-STORE PURCHASES<br/><span className="text-[12px] font-bold">(even pumps, robotic cleaners and spas)</span></p>
                  </div>
                </div>
              </div>
            </div>

            {priceCheck && <PriceCheckModal products={products} onClose={() => setPriceCheck(false)} />}
            {invoicePay && <InvoicePayModal clerk={clerk} onClose={() => setInvoicePay(false)} />}
            {eodOpen && <EndOfDay clerk={clerk} company={branding?.company} onClose={() => setEodOpen(false)} />}
            {petty === "out" && <PettyCashOut clerk={clerk} company={branding?.company} onClose={() => setPetty(null)} onDone={() => { setPetty(null); closeMenu(); }} />}
            {petty === "back" && <PettyCashBack company={branding?.company} onClose={() => setPetty(null)} onDone={() => { setPetty(null); closeMenu(); }} />}
          </div>
        )}
      </div>
    );
  }

  // ---------- REGISTER ----------
  return (
    <div data-testid="pos-register" className="rounded-xl p-3" style={{ background: "linear-gradient(180deg,#eaf3fb,#cfe3f5)" }}>
      <div className="text-center mb-2">
        {mode === "client" ? <span className="font-display text-2xl font-black text-blue-700 underline" data-testid="pos-client-name">{client?.name}</span> : <span className="font-display text-xl font-bold text-blue-700 underline">POS Counter Sale</span>}
        {mode === "client" && (client?.contacts || []).length > 0 && (
          <div className="mt-1 flex items-center justify-center gap-1.5 text-[12px]" data-testid="pos-attn">
            <span className="text-slate-600">Invoice to:</span>
            <select value={attn ? String((client.contacts).indexOf(attn)) : ""} onChange={(e) => setAttn(e.target.value === "" ? null : client.contacts[Number(e.target.value)])} data-testid="pos-attn-select" className="rounded border border-blue-300 bg-white px-2 py-0.5 text-[12px] font-semibold text-blue-800">
              <option value="">{client?.name} (account)</option>
              {client.contacts.map((c, i) => <option key={i} value={i}>{`${c.first_name || ""} ${c.surname || ""}`.trim()}{c.relationship ? ` — ${c.relationship}` : ""}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="bg-yellow-300 text-black font-black text-center px-4 py-2 rounded" data-testid="pos-ready">READY TO SCAN</div>
        <div className="flex gap-2">
          <button onClick={clear} data-testid="pos-clear" className="bg-[#e08a1e] text-white font-black px-6 py-2 rounded shadow hover:bg-[#c9791a]">CLEAR</button>
          <button onClick={() => setRefundOpen(true)} data-testid="pos-refund" className="bg-white text-red-600 font-black px-6 py-2 rounded border border-slate-300 shadow hover:bg-red-50">REFUND</button>
        </div>
        <button onClick={exitPos} data-testid="pos-exit" className="bg-red-600 text-white font-black px-6 py-2 rounded shadow hover:bg-red-700">EXIT POS</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr_260px] gap-3">
        {/* tiles */}
        <div>
          {tabs.length > 0 && tiles.length > 0 && (
            <div className="flex gap-1 border-b-2 border-[#123a63]" data-testid="pos-tabs">
              {tabs.map((t) => (
                <button key={t.id} onClick={() => setTabId(t.id)} data-testid={`pos-tab-${t.name.toLowerCase()}`}
                  className={`rounded-t-md px-4 py-1.5 text-[13px] font-black transition-colors ${t.id === tabId ? "bg-[#123a63] text-white" : "bg-white text-[#123a63] hover:bg-sky-50"}`}>{t.name}</button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-4 gap-1.5 max-h-[62vh] overflow-auto pr-1 pt-1.5" data-testid="pos-tiles">
            {(tiles.length ? displayTiles.filter((t) => t.tab_id === tabId) : displayTiles).map((t, i) => (
              <PosTile key={t.id || i} t={t} onClick={() => tapTile(t)} testid={`pos-tile-${t.id || i}`} />
            ))}
          </div>
          <button onClick={() => setManual(true)} data-testid="pos-manual-add" className="w-full mt-2 rounded bg-slate-200 py-2 text-[13px] font-black text-slate-700 hover:bg-slate-300">MANUALLY ADD ITEM</button>
          <button onClick={() => setDbPick(true)} data-testid="pos-db-add" className="w-full mt-1 rounded bg-slate-200 py-2 text-[13px] font-black text-slate-700 hover:bg-slate-300">ADD ITEM FROM DATABASE</button>
        </div>

        {/* cart or qty pad */}
        <div className="bg-white/80 border border-slate-300 rounded-md p-2 min-h-[50vh]">
          {qtyPad ? (
            <div className="text-center">
              <p className="font-black text-xl mt-4">{qtyPad.name}</p>
              <p className="font-black text-lg text-slate-700 mb-3">{money(qtyPad.unit_price)}</p>
              <p className="text-[12px] font-bold text-slate-500">QUANTITY</p>
              <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto mt-2">
                {QTY_KEYS.map((n) => <button key={n} onClick={() => confirmQty(n)} data-testid={`pos-qty-${n}`} className="rounded-md border border-slate-300 bg-slate-50 py-3 font-black text-lg hover:bg-sky-100">{n}</button>)}
              </div>
              <button onClick={() => setQtyPad(null)} className="mt-3 text-[12px] text-slate-500 hover:text-rose-600">Cancel</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-[1fr_90px_50px_90px_40px] gap-1 text-[12px] font-black text-slate-500 border-b pb-1"><span>Item</span><span className="text-right">Item Price</span><span className="text-center">Qty</span><span className="text-right">Net $</span><span /></div>
              <div className="divide-y divide-slate-100" data-testid="pos-cart">
                {cart.length === 0 && <p className="text-center text-slate-400 text-sm py-8">Cart is empty</p>}
                {cart.map((l, i) => (
                  <div key={i} className="grid grid-cols-[1fr_90px_50px_90px_40px] gap-1 items-center py-1.5 text-[13px]" data-testid="pos-cart-line">
                    <span className="truncate">{l.name}</span>
                    <span className="text-right">{money(l.unit_price)}</span>
                    <span className="text-center">{l.qty}</span>
                    <span className="text-right font-bold">{money(l.qty * l.unit_price)}</span>
                    <button onClick={() => removeLine(i)} className="text-rose-500 hover:text-rose-700 flex justify-center"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {discountPct > 0 && cart.length > 0 && (
                  <div className="grid grid-cols-[1fr_90px_50px_90px_40px] gap-1 items-center py-1.5 text-[13px]" data-testid="pos-discount-line">
                    <span className="font-bold text-emerald-700">{discountPct}% DISCOUNT APPLIED</span><span /><span /><span className="text-right font-bold text-emerald-700">-{money(discountAmt)}</span>
                    <button onClick={() => setDiscountPct(0)} data-testid="pos-discount-remove" className="text-rose-500 hover:text-rose-700 flex justify-center"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
              <div className="flex justify-end items-center gap-2 mt-3 border-t pt-2"><span className="text-[12px] font-bold text-slate-500">This Sale</span><span className="bg-yellow-200 px-4 py-1 rounded font-black text-lg" data-testid="pos-total">{money(total)}</span></div>
            </>
          )}
        </div>

        {/* payment column */}
        <div className="space-y-2">
          {mode === "client" && <div className="text-center bg-[#f2b705] text-[#123a63] font-black rounded py-1 text-[13px]" data-testid="pos-loyalty-band">LOYALTY CUSTOMER</div>}
          {mode === "client" && <button onClick={() => setPurchasesOpen(true)} data-testid="pos-client-purchases" className="w-full rounded bg-sky-700 text-white font-black py-2 text-[13px] hover:bg-sky-800 flex items-center justify-center gap-1.5"><Users className="h-4 w-4" /> CLIENT PURCHASES</button>}
          <div className="grid grid-cols-4 gap-1">
            {DISCOUNTS.map((d) => <button key={d} onClick={() => setDiscountPct(d)} data-testid={`pos-discount-${d}`} className={`rounded border py-2 text-[12px] font-black ${discountPct === d ? "bg-emerald-400 border-emerald-600" : "bg-emerald-100 border-emerald-300 hover:bg-emerald-200"}`}>{d}%<br/>OFF</button>)}
          </div>
          <button onClick={invoiceClient} data-testid="pos-invoice-client" className="w-full rounded bg-purple-700 text-white font-black py-3 text-left px-3 hover:bg-purple-800 flex items-center gap-2"><FileText className="h-5 w-5" /><span>INVOICE CLIENT<span className="block text-[10px] font-normal opacity-80">Create invoice & email to client</span></span></button>
          {mode === "client" && credit > 0 && <div className="text-center rounded bg-emerald-100 border border-emerald-400 text-emerald-800 font-black text-[12px] py-1" data-testid="pos-store-credit-balance">Store credit: ${credit.toFixed(2)}</div>}
          {total < 0 ? (
            <div className="space-y-2" data-testid="pos-refund-due">
              <div className="rounded bg-red-600 text-white text-center font-black py-2">REFUND DUE ${Math.abs(total).toFixed(2)}</div>
              <button onClick={() => finishSale([{ method: "cash", amount: total }])} data-testid="pos-refund-cash" className="w-full rounded bg-slate-100 border border-slate-300 py-3 font-black hover:bg-slate-200 flex items-center justify-center gap-2"><Banknote className="h-5 w-5" />CASH REFUND</button>
              <button onClick={() => finishSale([{ method: "eftpos", amount: total }])} data-testid="pos-refund-card" className="w-full rounded bg-white border border-slate-300 py-3 font-black hover:bg-sky-50 flex items-center justify-center gap-2"><CreditCard className="h-5 w-5" />REFUND TO CARD</button>
              <button onClick={() => (client ? finishSale([{ method: "store_credit", amount: total }]) : toast.error("Attach the client first (Loyalty search) to give store credit."))} data-testid="pos-refund-store-credit"
                className="w-full rounded bg-emerald-600 text-white py-3 font-black hover:bg-emerald-700">GIVE STORE CREDIT{client ? ` — ${client.name}` : ""}</button>
            </div>
          ) : (<>
          {mode === "client" && credit > 0 && total > 0 && credit >= total && <button onClick={() => finishSale([{ method: "store_credit", amount: total }])} data-testid="pos-pay-store-credit" className="w-full rounded bg-emerald-600 text-white py-3 font-black hover:bg-emerald-700">PAY WITH STORE CREDIT</button>}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setPay("cash")} data-testid="pos-pay-cash" className="rounded bg-slate-100 border border-slate-300 py-4 font-black hover:bg-slate-200 flex flex-col items-center"><Banknote className="h-6 w-6" />CASH</button>
            <button onClick={() => setPay("split")} data-testid="pos-pay-split" className="rounded bg-slate-100 border border-slate-300 py-4 font-black hover:bg-slate-200 flex flex-col items-center"><Split className="h-6 w-6" />SPLIT</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[["EFTPOS", "eftpos"], ["Visa", "visa"], ["MasterCard", "mastercard"], ["Amex", "amex"]].map(([c, m]) => <button key={m} onClick={() => finishSale([{ method: m, amount: total }])} data-testid={`pos-pay-${m}`} className="rounded bg-white border border-slate-300 py-3 text-[12px] font-black hover:bg-sky-50 flex items-center justify-center gap-1"><CreditCard className="h-4 w-4" />{c}</button>)}
          </div>
          </>)}
        </div>
      </div>

      {purchasesOpen && <PurchasesModal client={client} onBuyAgain={buyAgain} onClose={() => setPurchasesOpen(false)} />}
      {pay === "cash" && <CashModal total={total} onDone={finishSale} onClose={() => setPay(null)} />}
      {pay === "split" && <SplitModal total={total} onDone={finishSale} onClose={() => setPay(null)} />}
      {manual && <ManualModal onAdd={(l) => { addLine(l); setManual(false); }} onClose={() => setManual(false)} />}
      {dbPick && <DbPickModal products={products} onAdd={(line) => addOrMerge(line)} onClose={() => setDbPick(false)} />}
      {priceCheck && <PriceCheckModal products={products} onClose={() => setPriceCheck(false)} />}
      {completed && <Receipt sale={completed} branding={branding} onClose={() => { setCompleted(null); setCart([]); setClient(null); setAttn(null); setMode("counter"); setDiscountPct(0); setView("clerk"); }} />}
      {refundOpen && <RefundPopup onClose={() => setRefundOpen(false)} onAdd={(cat, amt) => { addLine({ name: `REFUND - ${cat}`, unit_price: -amt, qty: 1, product_id: null }); setRefundOpen(false); toast.success(`${cat} $${amt.toFixed(2)} added`); }} />}
    </div>
  );
}

const Overlay = ({ children, testid, onClose, wide }) => (
  <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid={testid} onClick={onClose}>
    <div className={`bg-white rounded-xl shadow-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-auto`} onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);
const Head = ({ title, onClose }) => <div className="flex justify-between items-center p-3 border-b"><span className="font-black text-slate-800">{title}</span><button onClick={onClose}><X className="h-5 w-5 text-slate-400 hover:text-slate-700" /></button></div>;

function PurchasesModal({ client, onBuyAgain, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api("GET", `/clients/${client.id}/purchases`).then(setRows).catch(() => setRows([])); }, [client.id]);
  const total = (rows || []).reduce((s, r) => s + (Number(r.qty) * Number(r.price) || 0), 0);
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-AU") : "");
  const cols = "grid grid-cols-[1fr_90px_45px_80px_80px_95px_90px] gap-2";
  return (
    <Overlay testid="pos-purchases-modal" onClose={onClose} wide>
      <div className="flex justify-between items-center p-3 border-b bg-[#eef4ec]">
        <span className="font-black text-slate-800">Client Purchases — {client.name}</span>
        <button onClick={onClose} data-testid="purchases-close"><X className="h-5 w-5 text-slate-400 hover:text-slate-700" /></button>
      </div>
      <div className="flex items-center justify-center py-2 bg-[#6b6b6b]">
        <span className="font-display text-3xl font-black text-yellow-300 underline" data-testid="purchases-total">{money(total)}</span>
      </div>
      <div className={`${cols} px-3 py-2 bg-[#7d7d7d] text-white text-[12px] font-black sticky top-0`}>
        <span>Item</span><span className="text-right">Item Price</span><span className="text-center">Qty</span><span className="text-right">Discount $</span><span className="text-right">Net $</span><span className="text-right">Date Purchased</span><span className="text-center">Buy Again</span>
      </div>
      <div className="max-h-[60vh] overflow-auto">
        {rows === null && <p className="text-center text-slate-400 py-8 text-sm">Loading…</p>}
        {rows && rows.length === 0 && <p className="text-center text-slate-400 py-8 text-sm">No purchases on record yet.</p>}
        {(rows || []).map((r, i) => (
          <div key={r.id || i} className={`${cols} px-3 py-2 text-[13px] items-center ${i % 2 ? "bg-[#d8d8d8]" : "bg-[#c9c9c9]"}`} data-testid="purchases-row">
            <span className="truncate text-slate-900">{r.product}</span>
            <span className="text-right">{money(r.price)}</span>
            <span className="text-center">{r.qty}</span>
            <span className="text-right">{money(0)}</span>
            <span className="text-right font-bold">{money(Number(r.qty) * Number(r.price))}</span>
            <span className="text-right">{fmt(r.date)}</span>
            <span className="flex justify-center">
              <button onClick={() => onBuyAgain && onBuyAgain(r)} data-testid={`purchases-buy-again-${i}`} title="Add to current sale" className="rounded bg-[#e08a1e] text-white text-[11px] font-black px-2 py-1 hover:bg-[#c9791a] flex items-center gap-1"><Plus className="h-3 w-3" /> Add</button>
            </span>
          </div>
        ))}
      </div>
    </Overlay>
  );
}

function PriceCheckModal({ products, onClose }) {
  const [q, setQ] = useState(""); const [sel, setSel] = useState(null);
  const list = rankParts(products, q, 20);
  return (
    <Overlay testid="pos-price-check-modal" onClose={onClose} wide>
      <Head title="Price Check" onClose={onClose} />
      <div className="p-4">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="price-check-search" placeholder="Scan item or search…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-2" />
        <div className="max-h-48 overflow-auto divide-y">{list.map((p) => <button key={p.id} onClick={() => setSel(p)} className="w-full text-left px-2 py-1.5 hover:bg-sky-50 text-sm flex justify-between"><span>{p.name}</span><span className="font-bold">{money(p.price)}</span></button>)}</div>
        {sel && <div className="mt-3 border-t pt-3 text-sm space-y-1" data-testid="price-check-detail"><p><b>Description:</b> {sel.name}</p><p><b>Brand:</b> {sel.brand || "—"}</p><p><b>PartsID:</b> {sel.sku || "—"}</p><p><b>Barcode:</b> {sel.barcode || "—"}</p><p><b>Price:</b> {money(sel.price)}</p><p><b>Qty in Stock:</b> {sel.qty ?? 0}</p></div>}
      </div>
    </Overlay>
  );
}

function LoyaltyModal({ onPick, onClose }) {
  const [clients, setClients] = useState([]); const [q, setQ] = useState("");
  useEffect(() => { api("GET", "/clients").then(setClients).catch(() => {}); }, []);
  const list = clients.filter((c) => { const s = q.toLowerCase(); return !s || c.name.toLowerCase().includes(s) || (c.company || "").toLowerCase().includes(s) || (c.phone || "").includes(q) || (c.loyalty_number || "").includes(q); }).slice(0, 40);
  return (
    <Overlay testid="pos-loyalty-modal" onClose={onClose}>
      <Head title="POS Loyalty Client Search" onClose={onClose} />
      <div className="p-4">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="loyalty-search" placeholder="Surname, company, phone/email, loyalty #…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-2" />
        <div className="max-h-64 overflow-auto divide-y">{list.map((c) => <button key={c.id} onClick={() => onPick(c)} data-testid={`loyalty-pick-${c.id}`} className="w-full text-left px-2 py-2 hover:bg-sky-50 text-sm"><span className="font-semibold">{c.name}</span> <span className="text-xs text-slate-400">{c.company || c.phone}</span>{c.discount_rate > 0 && <span className="ml-1 text-[10px] text-emerald-600 font-bold">· {c.discount_rate}%</span>}</button>)}
          {list.length === 0 && <p className="text-sm text-slate-400 py-3 text-center">No match.</p>}</div>
      </div>
    </Overlay>
  );
}

function InvoicePayModal({ clerk, onClose }) {
  const [inv, setInv] = useState([]); const [paying, setPaying] = useState(null);
  const load = useCallback(() => api("GET", "/sales?limit=100").then((s) => setInv(s.filter((x) => ["invoiced", "part_paid"].includes(x.status)))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const paidOf = (s) => (s.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
  // counter or phone payment: POSInvoiceResult2 (Cash / Eftpos / Visa / Mastercard / Amex) -> POS end-of-day tally
  if (paying) return <InvoicePaymentHow sale={paying} start="pos" clerk={clerk} reload={load} onClose={() => setPaying(null)} />;
  return (
    <Overlay testid="pos-invoice-pay-modal" onClose={onClose} wide>
      <Head title="Client Invoice Payment" onClose={onClose} />
      <div className="p-4 space-y-2">
        {inv.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No outstanding invoices.</p>}
        {inv.map((s) => (
          <div key={s.id} className="flex items-center gap-2 border rounded-lg p-2 text-sm" data-testid={`invoice-row-${s.id}`}>
            <span className="flex-1"><b>Invoice {s.sale_number}</b> · {s.client_name} · Total {money(s.total)} · Owing {money(s.total - paidOf(s))}</span>
            <button onClick={() => setPaying(s)} data-testid={`invoice-pay-${s.id}`} className="rounded bg-emerald-600 text-white px-3 py-1.5 font-bold">Pay</button>
          </div>
        ))}
      </div>
    </Overlay>
  );
}

function CashModal({ total, onDone, onClose }) {
  const [tendered, setTendered] = useState("");
  const t = parseFloat(tendered) || 0; const change = Math.max(t - total, 0);
  return (
    <Overlay testid="pos-cash-modal" onClose={onClose}>
      <Head title="Cash Payment" onClose={onClose} />
      <div className="p-4">
        <p className="text-sm">Total due <b>{money(total)}</b></p>
        <input autoFocus type="number" value={tendered} onChange={(e) => setTendered(e.target.value)} data-testid="cash-tendered" placeholder="Cash tendered" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-lg my-2" />
        <div className="flex flex-wrap gap-1.5 mb-2">{[20, 50, 100, total].map((v, i) => <button key={i} onClick={() => setTendered(String(v))} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold hover:bg-sky-100">{i === 3 ? "Exact" : `$${v}`}</button>)}</div>
        <p className="text-sm">Change <b className="text-emerald-600" data-testid="cash-change">{money(change)}</b></p>
        <button onClick={() => onDone([{ method: "cash", amount: t < total ? total : t }])} disabled={t < total} data-testid="cash-complete" className="mt-3 w-full rounded-lg bg-emerald-600 disabled:bg-slate-200 text-white font-bold py-3">Complete Sale</button>
      </div>
    </Overlay>
  );
}

function SplitModal({ total, onDone, onClose }) {
  const [rows, setRows] = useState([{ method: "cash", amount: "" }]);
  const paid = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  return (
    <Overlay testid="pos-split-modal" onClose={onClose}>
      <Head title="Split Payment" onClose={onClose} />
      <div className="p-4 space-y-2">
        <p className="text-sm">Total due <b>{money(total)}</b> · Allocated <b>{money(paid)}</b></p>
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2">
            <select value={r.method} onChange={(e) => setRows(rows.map((x, idx) => idx === i ? { ...x, method: e.target.value } : x))} className="rounded border border-slate-300 px-2 text-sm">{["cash", "eftpos", "visa", "mastercard", "amex", "account", "store_credit"].map((m) => <option key={m} value={m}>{m}</option>)}</select>
            <input type="number" value={r.amount} onChange={(e) => setRows(rows.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} placeholder="amount" className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
        ))}
        <button onClick={() => setRows([...rows, { method: "eftpos", amount: "" }])} className="text-xs text-sky-600 font-bold">+ Add method</button>
        <button onClick={() => onDone(rows.filter((r) => parseFloat(r.amount) > 0).map((r) => ({ method: r.method, amount: parseFloat(r.amount) })))} disabled={paid < total} data-testid="split-complete" className="w-full rounded-lg bg-emerald-600 disabled:bg-slate-200 text-white font-bold py-3">Complete Sale</button>
      </div>
    </Overlay>
  );
}

function ManualModal({ onAdd, onClose }) {
  const [f, setF] = useState({ name: "", price: "", qty: "1" });
  return (
    <Overlay testid="pos-manual-modal" onClose={onClose}>
      <Head title="Manually Add Item" onClose={onClose} />
      <div className="p-4 space-y-2">
        <input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="manual-name" placeholder="Item description" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" />
        <div className="flex gap-2"><input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="manual-price" placeholder="Price" className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm" /><input type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="manual-qty" placeholder="Qty" className="w-24 rounded border border-slate-300 px-3 py-2 text-sm" /></div>
        <button onClick={() => { if (!f.name) return; onAdd({ name: f.name, unit_price: parseFloat(f.price) || 0, qty: parseInt(f.qty) || 1, product_id: null }); }} data-testid="manual-add-confirm" className="w-full rounded-lg bg-emerald-600 text-white font-bold py-2.5">Add to Cart</button>
      </div>
    </Overlay>
  );
}

function DbPickModal({ products, onAdd, onClose }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [brand, setBrand] = useState("");
  const [group, setGroup] = useState("");
  const [added, setAdded] = useState({});
  const cats = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();
  const brands = [...new Set(products.map((p) => p.brand).filter(Boolean))].sort();
  const QUICK = [["Chemicals", "chem"], ["Spa", "spa"], ["Pool", "pool"], ["Gift Cards", "gift"]];
  const applyQuick = (g) => { setGroup((cur) => (cur === g ? "" : g)); setQ(""); setCat(""); setBrand(""); };
  const filtered = rankParts(products.filter((p) => (!cat || p.category === cat) && (!brand || p.brand === brand) && (!group || (p.pos_groups || []).includes(group))), q);
  const list = filtered.slice(0, 150);
  const add = (p) => { onAdd({ name: p.name, unit_price: p.price || 0, qty: 1, product_id: p.id }); setAdded((a) => ({ ...a, [p.id]: (a[p.id] || 0) + 1 })); };
  return (
    <Overlay testid="pos-db-modal" onClose={onClose} wide>
      <div className="flex justify-between items-center p-3 border-b"><span className="font-black text-slate-800">Add Part To POS</span><button onClick={onClose} data-testid="db-close"><X className="h-5 w-5 text-slate-400 hover:text-slate-700" /></button></div>
      <div className="p-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          {QUICK.map(([label, g]) => <button key={g} onClick={() => applyQuick(g)} data-testid={`db-quick-${g}`} className={`rounded-full text-[12px] font-bold px-3 py-1.5 ${group === g ? "bg-emerald-600 text-white" : "bg-sky-600 text-white hover:bg-sky-700"}`}>{label}</button>)}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="db-search" placeholder="Scan / Part Description / Part ID…" className="rounded border border-slate-300 px-3 py-2 text-sm" />
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="db-category" className="rounded border border-slate-300 px-2 py-2 text-sm"><option value="">All Categories</option>{cats.map((c) => <option key={c}>{c}</option>)}</select>
          <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="db-brand" className="rounded border border-slate-300 px-2 py-2 text-sm"><option value="">All Brands</option>{brands.map((b) => <option key={b}>{b}</option>)}</select>
        </div>
        <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500 px-1">
          <span data-testid="db-result-count">{filtered.length === 0 ? "No parts match" : `Showing ${list.length} of ${filtered.length}`}</span>
          {(group || cat || brand || q) && <button onClick={() => { setGroup(""); setCat(""); setBrand(""); setQ(""); }} data-testid="db-clear-filters" className="text-sky-600 hover:underline">Clear filters</button>}
        </div>
        <div className="max-h-[55vh] overflow-auto divide-y border rounded">
          {list.length === 0 && <p className="text-center text-slate-400 py-6 text-sm">No parts match.</p>}
          {list.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-2 py-2" data-testid={`db-row-${p.id}`}>
              <div className="h-12 w-12 shrink-0 bg-slate-50 border rounded overflow-hidden flex items-center justify-center">{p.image_url ? <img src={p.image_url.startsWith("http") ? p.image_url : `${base}${p.image_url}`} alt="" className="w-full h-full object-contain" /> : <span className="text-[9px] text-slate-300">no image</span>}</div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{p.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{p.brand || "—"} · ID {p.sku || "—"} · {p.barcode || "no barcode"} · Stock {p.qty ?? 0}</p>
              </div>
              <span className="text-sm font-bold w-20 text-right">{money(p.price)}</span>
              <button onClick={() => add(p)} data-testid={`db-add-${p.id}`} className="rounded bg-emerald-600 text-white text-[12px] font-black px-3 py-2 hover:bg-emerald-700 shrink-0">{added[p.id] ? `Added ×${added[p.id]}` : "Add To Cart"}</button>
            </div>
          ))}
        </div>
      </div>
    </Overlay>
  );
}
